import React, { useState, useCallback, useRef, useEffect } from 'react';
import { message, Checkbox, Spin } from 'antd';
import { FC110, scApi, SCSearchResult } from '../../../services/scApi';
import { dateToInput, inputToDate } from '../helpers';

interface TopSearchBarProps {
  formData: Partial<FC110>;
  isEditing: boolean;
  isFieldDisabled: (key: string) => boolean;
  getField: (key: keyof FC110) => FC110[keyof FC110];
  setField: (key: keyof FC110, value: FC110[keyof FC110]) => void;
  allIds: string[];
  onNavTo: (idx: number) => void;
  onSearchSelect?: (scNo: string) => void;
}

const PAGE_SIZE = 20;

// Default date range: 6 months ago ~ today
const getDefaultDateFrom = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  return d.toISOString().slice(0, 10);
};
const getDefaultDateTo = () => new Date().toISOString().slice(0, 10);

const TopSearchBar: React.FC<TopSearchBarProps> = ({
  formData, isEditing, isFieldDisabled,
  getField, setField,
  allIds, onNavTo, onSearchSelect,
}) => {
  const [keyword, setKeyword] = useState('');
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [useDateRange, setUseDateRange] = useState(true);
  const [dateFrom, setDateFrom] = useState(getDefaultDateFrom);
  const [dateTo, setDateTo] = useState(getDefaultDateTo);
  const [results, setResults] = useState<SCSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    if (showResults) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [showResults]);

  const doSearch = useCallback(async (searchPage = 1) => {
    setLoading(true);
    try {
      const res = await scApi.search(keyword, approvedOnly, searchPage, PAGE_SIZE,
        useDateRange ? dateFrom : undefined,
        useDateRange ? dateTo : undefined,
      );
      setResults(res.data || []);
      setTotal(res.total);
      setPage(searchPage);
      setShowResults(true);
    } catch {
      message.error('搜尋失敗');
    } finally {
      setLoading(false);
    }
  }, [keyword, approvedOnly, useDateRange, dateFrom, dateTo]);

  const handleSearch = useCallback(() => {
    if (isEditing) return;
    doSearch(1);
  }, [isEditing, doSearch]);

  const handleSelect = useCallback((scNo: string) => {
    setShowResults(false);
    setKeyword('');
    if (onSearchSelect) {
      onSearchSelect(scNo);
    } else {
      // Fallback: find in allIds
      const idx = allIds.findIndex(id => id === scNo);
      if (idx >= 0) onNavTo(idx);
    }
  }, [onSearchSelect, allIds, onNavTo]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <>
      {/* Search bar — independent block */}
      <div className="search-bar" ref={panelRef}>
        <div className="field-group">
          <input
            type="text"
            style={{ width: 180 }}
            placeholder="單號/客戶/產品代號"
            value={keyword}
            disabled={isEditing}
            onChange={e => setKeyword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <Checkbox
            checked={approvedOnly}
            disabled={isEditing}
            onChange={e => setApprovedOnly(e.target.checked)}
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
          >
            已核准
          </Checkbox>
          <Checkbox
            checked={useDateRange}
            disabled={isEditing}
            onChange={e => setUseDateRange(e.target.checked)}
            style={{ fontSize: 12, whiteSpace: 'nowrap' }}
          >
            入單日
          </Checkbox>
          <input
            type="date"
            style={{ width: 120, height: 24, fontSize: 12 }}
            value={dateFrom}
            disabled={isEditing || !useDateRange}
            onChange={e => setDateFrom(e.target.value)}
          />
          <span style={{ color: '#999' }}>~</span>
          <input
            type="date"
            style={{ width: 120, height: 24, fontSize: 12 }}
            value={dateTo}
            disabled={isEditing || !useDateRange}
            onChange={e => setDateTo(e.target.value)}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSearch}
            disabled={isEditing}
          >
            查詢
          </button>
        </div>

        {/* Search results dropdown panel */}
        {showResults && (
          <div className="search-results-panel">
            {loading ? (
              <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            ) : results.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 16, color: '#999' }}>無符合資料</div>
            ) : (
              <>
                <table className="search-results-table">
                  <thead>
                    <tr>
                      <th style={{ width: 100 }}>S/C 單號</th>
                      <th style={{ width: 90 }}>客戶代號</th>
                      <th>客戶名稱</th>
                      <th style={{ width: 90 }}>S/C 日期</th>
                      <th style={{ width: 80 }}>業務員</th>
                      <th style={{ width: 60 }}>幣別</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr
                        key={r.sc_no}
                        onDoubleClick={() => handleSelect(r.sc_no)}
                        title="雙擊選取"
                      >
                        <td>{r.sc_no}</td>
                        <td>{r.customer_code}</td>
                        <td className="ellipsis">{r.customer_name}</td>
                        <td>{r.sc_date || ''}</td>
                        <td>{r.salesperson}</td>
                        <td>{r.currency}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                <div className="search-results-footer">
                  <span>共 {total} 筆</span>
                  {totalPages > 1 && (
                    <span className="search-pager">
                      <button disabled={page <= 1} onClick={() => doSearch(page - 1)}>&lt;</button>
                      <span>{page} / {totalPages}</span>
                      <button disabled={page >= totalPages} onClick={() => doSearch(page + 1)}>&gt;</button>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Form info bar — connected to tabs below */}
      <div className="form-info-bar">
        <div className="field-group">
          <label data-contract-field="fc110_01">S/C-NO.</label>
          <input type="text" style={{ width: 150 }} readOnly value={getField('fc110_01') as string} data-contract-field="fc110_01" />
        </div>
        <div className="field-group">
          <label className="required">S/C日期</label>
          <input
            type="date" style={{ width: 150 }}
            disabled={isFieldDisabled('fc110_11')}
            value={dateToInput(getField('fc110_11') as string)}
            onChange={e => setField('fc110_11', inputToDate(e.target.value) as string)}
          />
        </div>
        <div className="field-group">
          <label className="required" data-contract-field="fc110_66">客戶下單日</label>
          <input
            type="date" style={{ width: 150 }}
            disabled={isFieldDisabled('fc110_66')}
            value={dateToInput(getField('fc110_66') as string)}
            onChange={e => setField('fc110_66', inputToDate(e.target.value) as string)}
          />
        </div>
        <div className="field-group">
          <label data-contract-field="fc110_63">入單日</label>
          <input
            type="text" style={{ width: 120 }} readOnly
            value={dateToInput(getField('fc110_63') as string)}
          />
        </div>
        <div className="checkbox-field">
          <input
            type="checkbox" id="chkInternal"
            disabled={!isEditing}
            checked={!!formData.fc110_75}
            onChange={e => setField('fc110_75', e.target.checked)}
          />
          <label htmlFor="chkInternal" data-contract-field="fc110_75">內部交易</label>
        </div>
      </div>
    </>
  );
};

export default React.memo(TopSearchBar);
